import { useState, useEffect, useRef } from "react";
import { Bell, Gear, BoxArrowRight } from "react-bootstrap-icons";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import "./AdminTopBar.css";

const AUTO_CLOSE_MS = 4000;

export default function AdminTopBar() {
  const { auth, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(3);

  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const timerRef = useRef(null);

  /* ---------------- AUTO CLOSE TIMER ---------------- */
  const startAutoCloseTimer = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setShowNotifications(false);
      setShowUserMenu(false);
    }, AUTO_CLOSE_MS);
  };

  const stopAutoCloseTimer = () => {
    clearTimeout(timerRef.current);
  };

  /* ---------------- CLICK OUTSIDE ---------------- */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        !notificationRef.current?.contains(e.target) &&
        !userMenuRef.current?.contains(e.target)
      ) {
        setShowNotifications(false);
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ---------------- CLOSE ON ROUTE CHANGE ---------------- */
  useEffect(() => {
    setShowNotifications(false);
    setShowUserMenu(false);
  }, [location.pathname]);

  /* ---------------- START TIMER WHEN OPEN ---------------- */
  useEffect(() => {
    if (showNotifications || showUserMenu) {
      startAutoCloseTimer();
    }
    return stopAutoCloseTimer;
  }, [showNotifications, showUserMenu]);

  return (
    <header className="admin-topbar">
      {/* Logo */}
      <div className="admin-logo" onClick={() => navigate("/admin")}>
        <span>Afro Admin</span>
      </div>

      {/* Right Section */}
      <div className="d-flex align-items-center gap-4 position-relative">
        {/* 🔔 Notifications */}
        <div className="position-relative" ref={notificationRef}>
          <div className="icon-wrapper">
            <Bell
              size={20}
              onClick={() => {
                setShowNotifications((v) => !v);
                setShowUserMenu(false);
                setUnreadCount(0); // optimistic clear
              }}
            />

            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </div>

          {showNotifications && (
            <div
              className="dropdown-panel animate"
              onMouseEnter={stopAutoCloseTimer}
              onMouseLeave={startAutoCloseTimer}
              style={{ width: 320 }}
            >
              <div className="p-3 border-bottom fw-semibold">Notifications</div>
              <ul className="list-group list-group-flush">
                <li className="list-group-item">📩 New messages</li>
                <li className="list-group-item">💸 Refund requested</li>
                <li className="list-group-item">⚠️ System alert</li>
              </ul>
              <div className="text-center p-2">
                <small className="text-muted">View all</small>
              </div>
            </div>
          )}
        </div>

        {/* 👤 User Menu */}
        <div className="position-relative" ref={userMenuRef}>
          <div
            className="d-flex align-items-center gap-2 user-trigger"
            onClick={() => {
              setShowUserMenu((v) => !v);
              setShowNotifications(false);
            }}
          >
            <div className="avatar">{auth?.firstName?.[0] || "A"}</div>
            <span className="fw-medium">{auth?.firstName || "Admin"}</span>
          </div>

          {showUserMenu && (
            <div
              className="dropdown-panel animate"
              onMouseEnter={stopAutoCloseTimer}
              onMouseLeave={startAutoCloseTimer}
              style={{ width: 220 }}
            >
              <button className="dropdown-item d-flex gap-2 p-2">
                <Gear /> Settings
              </button>
              <hr className="my-1" />
              <button
                className="dropdown-item d-flex gap-2 p-2 text-danger"
                onClick={logout}
              >
                <BoxArrowRight /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
